import { lazy, useEffect, useState } from "react";
import Axios from "axios";
import { Navigate, useParams } from "react-router-dom";

// project import
import Loadable from '@/components/Loadable';
import { ModuleGate } from '@/components/ModuleGate';
import MainLayout from '@/layout/MainLayout';
import NotificationPage from '@/components/NotificationPage';
import Calendar from '@/components/Calendar';
import RecipeConfig from '@/pages/Batch-Processing/RecipeConfig';
import Batch from '@/pages/Batch-Processing/Recipe/Batch';
import AuditLogsReport from '@/pages/Batch-Processing/AuditLogsReport';
import ChangeControl from '@/pages/Batch-Processing/ChangeControl';
import RecipeApprovalDemo from '@/pages/Batch-Processing/RecipeApprovalDemo';
import RecipeVersioning from '@/pages/Batch-Processing/RecipeVersioning';
import StandardBlockVersioning from '@/pages/Batch-Processing/StandardBlockVersioning';
import AddIngredient from '@/pages/Batch-Processing/Recipe/AddIngredient';
import AddData from '@/pages/Batch-Processing/Recipe/AddData';
import Product from '@/pages/Batch-Processing/Recipe/Product';
import SampleTesting from '@/pages/Batch-Processing/Recipe/SampleTesting';
import BatchLogBook from '@/pages/Batch-Processing/BatchLogBook';
import CleaningLogs from '@/pages/Batch-Processing/CleaningLogs';
import EventLogs from '@/pages/Batch-Processing/EventLogs';
import EquipmentLogs from '@/pages/Batch-Processing/EquipmentLogs';
import ScheduledBatches from '@/pages/Batch-Processing/Recipe/ScheduledBatches';
import MasterLotPage from '@/pages/Batch-Processing/Recipe/MasterLotPage';
import BmrTrackingRedirect from '@/pages/Batch-Processing/Recipe/bmr-details/BmrTrackingRedirect';
import Batch360 from '@/pages/Batch-Processing/Recipe/Batch360';
import BatchProcessDetails from '@/pages/Batch-Processing/Recipe/BatchProcessDetails';
import AddSample from '@/pages/Batch-Processing/Recipe/AddSample';
import ReportSkeleton from '@/pages/Batch-Processing/ReportSkeleton';
import ReportTemplateConfig from '@/pages/Batch-Processing/Configurations/ReportTemplateConfig';
import UserConfig2 from '@/pages/Batch-Processing/Configurations/UserConfig2';
import UserManagementDashboard from '@/pages/Batch-Processing/Configurations/UserManagementDashboard';
import TrainingConfiguration from '@/pages/Batch-Processing/Configurations/TrainingConfiguration';
import CompanySettings from '@/pages/Batch-Processing/Configurations/CompanySettings';
import ShiftManagement from '@/pages/Batch-Processing/Configurations/ShiftManagement';
import GenerateQRs from '@/pages/Batch-Processing/Configurations/GenerateQRs';
import Shift from '@/pages/Batch-Processing/shift-details/Shift';
import EmployeeAvailability from '@/pages/Batch-Processing/shift-details/EmployeeAvailability';
import EmployeePreferences from '@/pages/Batch-Processing/shift-details/EmployeePreferences';
import ShiftsWorkspace from '@/pages/Batch-Processing/shift-details/ShiftsWorkspace';
import DAGFlowCopy from '@/pages/dashboard/ArchiMateFlow';
import ScheduleNotificationPage from '@/components/ScheduleNotificationPage';
import AlertNotificationPage from '@/components/AlertNotificationPage';
import Sensor from '@/pages/Batch-Processing/Configurations/SensorView';
import SensorMonitoringDashboard from '@/components/sensors/SensorMonitoringDashboard';
import ProcessEdit from '@/pages/Batch-Processing/Recipe/EditProcess';
import Graph from '@/pages/Batch-Processing/Configurations/Graph';
import GraphNew from '@/pages/Batch-Processing/Configurations/Graph_new';
import TicketDetails from '@/pages/Batch-Processing/TicketDetails';
import ApproveRequest from '@/pages/Batch-Processing/ApproveRequest';
import StandardChecklist from '@/pages/Batch-Processing/StandardChecklist';
import LineClearance from '@/pages/Batch-Processing/LineClearance';
import PackagingLogs from '@/pages/Batch-Processing/Recipe/Packaging/PackagingLog';
import PackDispatchLogs from '@/pages/Batch-Processing/Recipe/Packaging/DispatchHistory';
import CheckCleaning from '@/pages/Batch-Processing/Recipe/CheckCleaning';
import CleaningTypes from '@/pages/Batch-Processing/Configurations/CleaningTypes';
import ProcessControlConfig from '@/pages/Batch-Processing/Configurations/ProcessControlConfig';
import MBMR from '@/pages/Batch-Processing/mbmr/Main';
// RV9 · the designer as one ordered list of typed steps, replacing the ten tabs.
import RecordDesigner from '@/pages/Batch-Processing/mbmr/RecordDesigner';
import RecordDesignerPicker from '@/pages/Batch-Processing/mbmr/RecordDesignerPicker';
import RecordView from '@/pages/Batch-Processing/mbmr/RecordView';
// RV9a · the operation-type library, grouped and with its coverage visible. The old table
// listed 137 rows and could not answer "is there already one of these?".
import OperationTypeLibrary from '@/pages/Batch-Processing/Configurations/OperationTypeLibrary';
import OperationTypeView from '@/pages/Batch-Processing/Configurations/OperationTypeView';

import PlantSelection from '@/pages/LandingPage/PlantSelection';

/** The param is `recipe_id`, not `recipeId`: the designer mounts the SAME process tree that
 *  /process-config uses, and that component reads `useParams().recipe_id`. Renaming it here
 *  would mean forking the tree, which is the thing this rebuild exists to avoid. */
const RecordDesignerRoute = () => {
  const { recipe_id } = useParams();
  return <RecordDesigner recipeId={recipe_id} />;
};
// A Process Library block, authored with the same designer. It is a process with no recipe,
// so it has its own id in the URL rather than a recipe's.
const MasterRecordFromDocument = Loadable(lazy(() => import('@/pages/ai/MasterRecordFromDocument')));
const SopChangeImpact = Loadable(lazy(() => import('@/pages/ai/SopChangeImpact')));
const LibraryProcessDesigner = Loadable(
  lazy(() => import("@/pages/Batch-Processing/mbmr/LibraryProcessDesigner"))
);
const LibraryProcessDesignerRoute = () => {
  const { process_id } = useParams();
  return <LibraryProcessDesigner processId={process_id} />;
};
// render - dashboard
const Dash = Loadable(
  lazy(() => import("@/pages/dashboard/GrafanaDashboard1.js"))
);
// const MachineDetails = Loadable(lazy(() => import('@/pages/Preventive-maintenance/machine-details/MachineDetails')));


const AccessControl = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/AccessControl"))
);

const PasswordPolicy = Loadable(lazy(() => import("@/pages/Administration/PasswordPolicy")));
const ActiveSessions = Loadable(lazy(() => import("@/pages/Administration/Sessions/ActiveSessions")));
const SessionHistory = Loadable(lazy(() => import("@/pages/Administration/Sessions/SessionHistory")));
const UserSessionsView = Loadable(lazy(() => import("@/pages/Administration/Sessions/UserView")));
const DirectoryAccess = Loadable(lazy(() => import("@/pages/Administration/DirectoryAccess")));
const PlantConfiguration = Loadable(
  lazy(() => import("@/pages/Administration/PlantConfiguration"))
);

const HierarchyDefinition = Loadable(
  lazy(() => import("@/pages/Administration/HierarchyDefinition"))
);
const HierarchyLevelsDefinition = Loadable(
  lazy(() => import("@/pages/Administration/HierarchyLevelsDefinition"))
);

const AddRecipeForm = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/AddRecipeForm"))
);

const AllProcessesByRecipeId = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/PendingRecipe"))
);

const RecipeMetaData = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/RecipeMetaData"))
);

const ApproveProcess = Loadable(
  lazy(() => import("@/pages/Batch-Processing/ApproveProcess"))
);
//const Audit = Loadable(lazy(() => import("@/pages/Batch-Processing/Audit")));
const Audit = Loadable(
  lazy(() => import("@/pages/Batch-Processing/AuditReport"))
);

const ChangeHistoryAudit = Loadable(
  lazy(() => import("@/pages/Batch-Processing/ChangeHistoryAudit"))
);

const UserProfile = Loadable(lazy(() => import("@/components/UserProfile")));

const BOM = Loadable(lazy(() => import("@/pages/Batch-Processing/BOM")));
const EditBatch = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/EditBatch"))
);
const UrlsConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/UrlsConfig"))
);
const ProductConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/Product"))
);
const ReportTemplateDesigner = Loadable(
  lazy(() => import("@/pages/Batch-Processing/reports/ReportTemplateDesigner"))
);
const ReportTemplateBuilder = Loadable(
  lazy(() => import("@/pages/Batch-Processing/reports/ReportTemplateBuilder"))
);
const ReportBuilder = Loadable(
  lazy(() => import("@/pages/Batch-Processing/reports/ReportBuilder"))
);
const ApprovalProcesses = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Approvals/ApprovalProcesses"))
);
const MasterFormulaRecords = Loadable(
  lazy(() => import("@/pages/Batch-Processing/MFR/MasterFormulaRecords"))
);
// SCALE lane: derive at size, structure from a precedent.
const ScaledCopy = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/ScaledCopy"))
);
const Inventory = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/Inventory"))
);
const Equipment = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/Equipment"))
);
const SampleConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/SampleConfig"))
);
const TestingQC = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/TestingQC"))
);
const Machine = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/MachineEnhanced"))
);
const MachineTypeConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/MachineTypeConfig"))
);
const Parameter = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/Parameter"))
);
const ControlCategoryConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/ControlCategoryConfig"))
);
const ProcessControlParameterConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/ProcessControlParameterConfig"))
);

const TransferPathConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/TransferPathConfig"))
);
const Licence = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/LicenceConfig"))
);
const RecipeApproval = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/RecipeApproval"))
);
// Master Records "Approvals" — Recipe Approval, Process Approval and Recipe Rollout under one
// tabbed page (the three routes below still exist individually for deep links). Named
// MasterApprovalsTabs, not Approvals — that name is already taken (Recipe/Approvals.js, /batch-approval).
const RecipeBuildingBlocks = Loadable(
  lazy(() => import("@/pages/Batch-Processing/RecipeBuildingBlocks"))
);
const MasterApprovalsTabs = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Approvals"))
);
// 2026-10-29 (owner) · menu streamlining: the pages one menu entry opens, each holding existing pages as tabs
// or sections (pages/hubs/MenuHubs.jsx). The hosted pages keep their own routes.
const hub = (name) => Loadable(lazy(() => import("@/pages/hubs/MenuHubs").then((m) => ({ default: m[name] }))));
const EquipmentAndRoomsHub = hub("EquipmentAndRoomsHub");
const ReviewReleaseHub = hub("ReviewReleaseHub");
const TraceabilityHub = hub("TraceabilityHub");
const CpvHub = hub("CpvHub");
const AuditTrailHub = hub("AuditTrailHub");
const ReportsHub = hub("ReportsHub");
const TrendAnalysisHub = hub("TrendAnalysisHub");
const MasterRecordsHub = hub("MasterRecordsHub");
const ProductsHub = hub("ProductsHub");
const PlantEquipmentSettings = hub("PlantEquipmentSettings");
const QualitySetupSettings = hub("QualitySetupSettings");
const PeopleAccessSettings = hub("PeopleAccessSettings");
const ApprovalWorkflowsSettings = hub("ApprovalWorkflowsSettings");
const SystemSettings = hub("SystemSettings");
const IntegrationsSettings = hub("IntegrationsSettings");

const DcsConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/DcsConfig"))
);

const RecipeView = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/ViewRecipe"))
);

// Support MBMR — recipes with no product (product IS NULL). Reuses the recipe
// list view, scoped to support recipes via the `support_mbmr` prop.
const SupportMBMR = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/ViewRecipe"))
);

// Library — Process/Stages, Process Library Approvals, Operations, Forms, Checklist and Formula
// under one tabbed page (the six routes below still exist individually for deep links).
const Library = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Library"))
);

// Process Library — reusable, recipe-less processes catalog.
const ProcessLibrary = Loadable(
  lazy(() => import("@/pages/Batch-Processing/ProcessLibrary"))
);

// Process Library Approvals — pending Send-to-Approval queue for library processes.
const ProcessLibraryApproval = Loadable(
  lazy(() => import("@/pages/Batch-Processing/ProcessLibraryApproval"))
);

// Review by Exception (/qa-review-dashboard). Backed by get_qa_review_batches +
// get_qa_review_summary. ReviewException renders what those endpoints actually return —
// score, verdict, release blockers, the graded findings and the IPC summary — and writes the
// review (POST give_batch_review). The older QAReviewDashboard.js read the same payload but
// showed only "sections / passed / exceptions" and landed on a blank Empty until the reviewer
// hand-picked a batch, so it is no longer routed.
// RBE-QUEUE-1: /qa-review-dashboard opens on the review queue (completed batches not yet review-signed);
// ?batch_number=… opens that batch's exception register (ReviewException).
const ReviewByException = Loadable(
  lazy(() => import("@/pages/Batch-Processing/ReviewQueue"))
);

const PendingRecipe = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/PendingRecipe"))
);
const ProcessApproval = Loadable(
  lazy(() => import("@/pages/Batch-Processing/ApproveProcess"))
);
const ProdRecipeApproval = Loadable(
  lazy(() => import("@/pages/Batch-Processing/ProdRecipeApproval"))
);
const OperationApproval = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/OperationApproval"))
);
const ProcessApprovalDetail = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/ProcessApprovalDetail"))
);

const ApplicationSettings = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Configurations/ApplicationSettings")
  )
);
const DynamicFlow = Loadable(
  lazy(() => import("@/pages/Batch-Processing/flow/DynamicFlow"))
);

const StandardBlockForm = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/StandardBlockForm"))
);

const TemporaryOperationForm = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/TemporaryOperationForm"))
);

const RulesConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Rules/RulesConfig"))
);

const RulesForm = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Rules/RulesForm"))
);

const IngredientsRules = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Rules/IngredientsRules"))
);

const MonitoringRules = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Rules/MonitoringRules"))
);

const SamplingRules = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Rules/SamplingRules"))
);

const ProductRules = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Rules/ProductRules"))
);

const ActionListRules = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Rules/ActionListRules"))
);

const ChecklistRules = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Rules/ChecklistRules"))
);

const LineClearanceRules = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Rules/LineClearanceRules"))
);

const APICalculationRules = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Rules/APICalculationRules"))
);

const MachineSetupRules = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Rules/MachineSetupRules"))
);

const PackagingConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Recipe/Packaging/PackagingConfig"))
);
// LIFECYCLE-01 step 6 · My actions — the personal lifecycle inbox across MFR, MBMR/MBPR and batches.
const MyActions = Loadable(lazy(() => import("@/pages/lifecycle/MyActions")));
const TemplateManager = Loadable(lazy(() => import("@/pages/TemplateManager")));
const ViewPackRecipe = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Recipe/Packaging/ViewPackageRecipe")
  )
);

const ProcessManage = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Recipe/Packaging/ProcessManagement")
  )
);

const ContineousProd = Loadable(
  lazy(() => import("@/components/ContinuousProduction"))
);

// Add the FormBuilder with Loadable wrapper
const FormBuilder = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/FormBuilder"))
);

// 2026-10-28 (owner) · a Standard Block authored in the record designer.
// 2026-10-29 (owner) · the plant's signature names (Recipe Building Blocks → Step Content → Signatures)
const SignatureNames = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/SignatureNames"))
);
const StandardBlockDesigner = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/StandardBlockDesigner"))
);
const StandardBlockDetails = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Configurations/StandarBlockDetails")
  )
);

const RefillingLogs = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/RefillingLogs"))
);


const AreaConfig = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Recipe/PharmaFlow/AreaConfiguration")
  )
);


const DispensingSchedule = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Dispensing/DispensingSchedule"))
);

const BatchGantt = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Dashboard/BatchGantt"))
);


const WorkflowBuilder = Loadable(
  lazy(() => import("@/pages/Batch-Processing/flow/WorkFlowNew"))
);
// CT-07: the Control Tower — one menu item with tabs; Plant Cockpit is its Overview tab.
const ControlTower = Loadable(
  lazy(() => import("@/pages/control-tower/ControlTower"))
);
const MBMRDashboard = Loadable(
  lazy(() => import("@/pages/dashboard/dashboard_data/RoleBasedDashboard"))
);
const RoomConfig = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Configurations/RoomWithBoothsConfig")
  )
);

const MasterDataConfig = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Configurations/MasterDataConfig")
  )
);

const IntegrationsHub = Loadable(
  lazy(() => import("@/pages/Integrations/IntegrationsHub"))
);
const ConnectorFlows = Loadable(
  lazy(() => import("@/pages/Integrations/ConnectorFlows"))
);
const IntegrationStatus = Loadable(
  lazy(() => import("@/pages/Integrations/IntegrationStatus"))
);
const ConnectStudio = Loadable(
  lazy(() => import("@/pages/Integrations/connect/ConnectStudio"))
);
const OperationTypeConfig = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Configurations/OperationTypeConfig")
  )
);
const ProcessTypeConfig = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Configurations/ProcessTypeConfig")
  )
);
const OutputFormTypeConfig = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Configurations/OutputFormTypeConfig")
  )
);
const VariantTypeConfig = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Configurations/VariantTypeConfig")
  )
);
const MaterialTypeConfig = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Configurations/MaterialTypeConfig")
  )
);
const MaterialStateConfig = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Configurations/MaterialStateConfig")
  )
);
const UnitsConfig = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/Configurations/UnitsConfig")
  )
);

// UNIT_COUNT visual-inspection module (unit_count_operations_spec.md §6)
const InspectionLotBoard = Loadable(
  lazy(() => import("@/pages/inspection/LotBoard"))
);
const DefectCodes = Loadable(
  lazy(() => import("@/pages/inspection/DefectCodes"))
);
// INSP-01 D5/D6 (owner, 12 Oct 2026): a product's defect sets are edited with its inspection specification
const InspectionSpecification = Loadable(
  lazy(() => import("@/pages/inspection/InspectionSpecification"))
);
const InspectionBoardPicker = Loadable(
  lazy(() => import("@/pages/inspection/InspectionBoardPicker"))
);
// FR-05: the plant's inspection stations (booths, measuring machines, QA and packaging desks), read-only
const StationMaster = Loadable(
  lazy(() => import("@/pages/inspection/StationMaster"))
);

// Fix-wave lane pages (dashboards, live execution, SPC, SoP/attachments, numbering, audit review, locations)
const ShiftBoard = Loadable(lazy(() => import("@/pages/dashboard/ShiftBoard")));
const DashboardThresholds = Loadable(lazy(() => import("@/pages/dashboard/Thresholds")));
const EquipmentRooms = Loadable(lazy(() => import("@/pages/dashboard/EquipmentRooms")));
const LiveBatchMonitor = Loadable(lazy(() => import("@/pages/execution/LiveBatchMonitor")));
const ControlCharts = Loadable(lazy(() => import("@/pages/quality/ControlCharts")));
const ApqrGraphStudio = Loadable(lazy(() => import("@/apps/APQRApp/pages/ApqrGraphStudio")));
const ApqrDataTables = Loadable(lazy(() => import("@/apps/APQRApp/pages/ApqrDataTables")));
// CPV-01 S6 — routed, no menu entry until W1-10 (the serial menu file); reached by URL only.
const CpvHome = Loadable(lazy(() => import("@/pages/quality/cpv/CpvHome")));
const CpvRiskBands = Loadable(lazy(() => import("@/pages/quality/cpv/CpvRiskBands")));
const CpvPlans = Loadable(lazy(() => import("@/pages/quality/cpv/CpvPlans")));
const CpvPlanDetail = Loadable(lazy(() => import("@/pages/quality/cpv/CpvPlanDetail")));
const CpvPlanCharts = Loadable(lazy(() => import("@/pages/quality/cpv/CpvPlanCharts")));
const CpvParameterPage = Loadable(lazy(() => import("@/pages/quality/cpv/CpvParameterPage")));
const CpvSignals = Loadable(lazy(() => import("@/pages/quality/cpv/CpvSignals")));
const CpvSignal = Loadable(lazy(() => import("@/pages/quality/cpv/CpvSignal")));
const NumberingConfig = Loadable(lazy(() => import("@/pages/Administration/NumberingConfig")));
const SopDocuments = Loadable(lazy(() => import("@/pages/Administration/SopDocuments")));
const RequiredAttachments = Loadable(lazy(() => import("@/pages/Administration/RequiredAttachments")));
const AuditTrailReview = Loadable(lazy(() => import("@/pages/Administration/AuditTrailReview")));
const RecordExports = Loadable(lazy(() => import("@/pages/Administration/RecordExports")));
const ReportDesignerList = Loadable(lazy(() => import("@/pages/ReportDesigner").then((m) => ({ default: m.ReportDesignerListRoute }))));
const ReportDesignerDesign = Loadable(lazy(() => import("@/pages/ReportDesigner").then((m) => ({ default: m.ReportDesignerDesignRoute }))));
const ReportDesignerPreview = Loadable(lazy(() => import("@/pages/ReportDesigner").then((m) => ({ default: m.ReportDesignerPreviewRoute }))));
const ReportDesignerPart = Loadable(lazy(() => import("@/pages/ReportDesigner").then((m) => ({ default: m.ReportDesignerPartRoute }))));
const PlantHierarchyManager = Loadable(lazy(() => import("@/pages/Administration/PlantHierarchyManager")));
const LocationNodePage = Loadable(lazy(() => import("@/pages/Administration/LocationNodePage")));
const LocationLevelSetupPage = Loadable(lazy(() => import("@/pages/Administration/LocationLevelSetupPage")));

// New MBMRConfig page Changes

const MBMRConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/MBMRConfig"))
);

const Workflow = Loadable(lazy(() => import("@/pages/workflow-creation/index")));

const WorkflowsList = Loadable(
  lazy(() => import("@/pages/workflow-creation/WorkflowsList"))
);

const BMRWorkflowsPage = Loadable(
  lazy(() => import("@/pages/workflow-creation/BMRWorkflowsPage"))
);

const BMRWorkflowBuilderPage = Loadable(
  lazy(() => import("@/pages/workflow-creation/BMRWorkflowBuilderPage"))
);

const UpdateWorkflow = Loadable(
  lazy(() => import("@/pages/workflow-creation/UpdateWorkflow"))
);

const FormTemp = Loadable(lazy(() => import("@/pages/dynamic-form")));

const CreateForm = Loadable(
  lazy(() => import("@/pages/dynamic-form/CreateForm"))
);

const TicketList = Loadable(lazy(() => import("@/pages/ticket/TicketList")));

const TicketCreation = Loadable(
  lazy(() => import("@/pages/ticket/ticket-creation"))
);

const TicketMainpage = Loadable(
  lazy(() => import("@/pages/ticket/TicketMainpage"))
);

const EBMRControlCharts = Loadable(
  lazy(() => import("@/pages/ticket/eBMRControlCharts"))
);

const DetailsPage = Loadable(lazy(() => import("@/pages/DetailsPage")));

const ViewForm = Loadable(lazy(() => import("@/pages/dynamic-form/ViewForm")));

const TestTypeField = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/AddTestType"))
);

const ApiCalcField = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/ApiCalcField"))
);
const BatchScoring = Loadable(lazy(() => import("@/pages/quality/BatchScoring")));
const FormulaLibrary = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/FormulaLibrary"))
);
const FormulaDomainMappingConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/FormulaDomainMappingConfig"))
);
const ClearanceProfiles = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/ClearanceProfiles"))
);
const ClearanceType = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/ClearanceType"))
);
const BatchNumberFormatConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/BatchNumberFormatConfig"))
);
const MfrMbmrLockConfig = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/MfrMbmrLockConfig"))
);
const SystemIntegration = Loadable(
  lazy(() =>
    import("@/pages/Batch-Processing/MBMRConfig/ExternalSystemIntegration")
  )
);

const WorkflowAutomation = Loadable(
  lazy(() => import("@/pages/workflow-automation/WorkflowAutomation"))
);

const WorkflowAutomationBuilder = Loadable(
  lazy(() => import("@/pages/workflow-automation/WorkflowBuilder"))
);

const GenealogyTree = Loadable(
  lazy(() => import("@/components/genealogy/GenealogyTree"))
);

const RecallAnalysis = Loadable(
  lazy(() => import("@/components/genealogy/RecallAnalysis"))
);

const BatchGenealogyFull = Loadable(
  lazy(() => import("@/pages/Batch-Processing/BatchGenealogy"))
);

// Batch lifecycle workflow timeline (Initiation -> Release Approval ->
// Pre-Checklist -> Execution -> Completion -> QA Release Review -> Release)
const BatchWorkflowTimeline = Loadable(
  lazy(() => import("@/pages/Batch-Processing/BatchWorkflow/BatchWorkflowTimeline"))
);

// ==============================|| MAIN ROUTING ||============================== //
const ActionListField = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/ActionListField"))
);

// Checklist Fields — master preset list for the Checklist sub-operation's
// "Checklist Items" dropdown, mirroring ActionListField above.
const ChecklistFields = Loadable(
  lazy(() => import("@/pages/Batch-Processing/Configurations/ChecklistFields"))
);


// Role-aware landing (F9, EXV-038). The index route is where a person's work is: the server works it out from
// their own module flags and POLICY rows (`get_landing_hint`: the review queue for a reviewer, the sign-off queue
// for a counter-signer while it holds rows, the batch list for the floor) — never from role names in the client.
// With no hint (none applies, or the call failed) the existing default stays. Only the "/" index is affected.
const RoleLanding = () => {
  const [hint, setHint] = useState({ done: false, landing: null });
  useEffect(() => {
    let alive = true;
    Axios.get('/bp/get_landing_hint', { headers: { Authorization: `Bearer ${localStorage.getItem('Token') || ''}` } })
      .then((res) => alive && setHint({ done: true, landing: res?.data?.landing || null }))
      .catch(() => alive && setHint({ done: true, landing: null }));
    return () => {
      alive = false;
    };
  }, []);
  if (!hint.done) return null;
  if (hint.landing) return <Navigate to={hint.landing} replace />;
  return <Dash />;
};

const MainRoutes = () => {
  return {
    path: "/",
    element: <MainLayout />,
    children: [
      {
        path: "/",
        element: <RoleLanding />,
      },
      {
        path: "/dash",
        element: <Dash />,
      },
      {
        path: "/user-profile",
        element: <UserProfile />,
      },
      {
        path: "/viewRecipe",
        element: <RecipeConfig />,
      },
      {
        path: "/scheduling",
        // "Issue Batch" is a modal on /batches, not a page of its own; old
        // links and bookmarks land there with the modal open.
        element: <Navigate to="/batches?issue=1" replace />,
      },
      {
        path: "/result",
        // Report Details retired (owner, 7 Oct 2026): old links land on the certified record exports.
        element: <Navigate to="/record-exports" replace />,
      },
      {
        path: "/approve-request",
        element: <ApproveRequest />,
      },
      {
        path: "/standard-checklist",
        element: <StandardChecklist />,
      },
      {
        path: "/line-clearance",
        element: <LineClearance />,
      },
      {
        path: "/ticket-details",
        element: <TicketDetails />,
      },
      {
        path: "/report",
        element: <ReportSkeleton />,
      },
      {
        path: "/audit-logs",
        element: <AuditLogsReport />,
      },
      {
        path: "/change-control",
        element: <ChangeControl />,
      },
      {
        path: "/recipe-versioning",
        element: <RecipeVersioning />,
      },
      {
        path: "/standard-block-versioning",
        element: <StandardBlockVersioning />,
      },
      {
        path: "/recipe-approval-workflow",
        element: <RecipeApprovalDemo />,
      },
      {
        path: "/report-template-config",
        element: <ReportTemplateDesigner />,
      },
      {
        path: "/report-template-config/form",
        element: <ReportTemplateBuilder />,
      },
      {
        path: "/report-template-config/legacy",
        element: <ReportTemplateConfig />,
      },
      {
        path: "/batches",
        element: <ScheduledBatches />,
      },
      {
        path: "/master-lot",
        element: <MasterLotPage />,
      },
      {
        path: "/create-demand",
        element: <MasterLotPage />,
      },
      {
        path: "/dispensing-scheduling",
        element: <DispensingSchedule />,
      },
      {
        path: "/batch-gantt",
        element: <BatchGantt />,
      },
      {
        path: "/batch-process-details/:batch_number",
        element: <BatchProcessDetails />,
      },
      {
        // EXV-027: retired — redirects to BMR Details → Ongoing
        path: "/bmr-tracking",
        element: <BmrTrackingRedirect />,
      },
      {
        path: "/batch-traceability",
        element: <Batch360 />,
      },
      {
        path: "/inspection/board",
        element: <InspectionBoardPicker />,
      },
      {
        path: "/inspection/board/:batch_number",
        element: <InspectionLotBoard />,
      },
      // INSP-01 (owner, 12 Oct 2026): the defect codes moved to the product's Inspection Specification; the plant-wide
      // catalogue of standard codes is the Defect Code Library. The old route opens the specification.
      { path: "/defect-codes", element: <Navigate to="/inspection-specification" replace /> },
      { path: "/defect-code-library", element: <DefectCodes /> },
      { path: "/inspection-stations", element: <StationMaster /> },
      { path: "/inspection-specification", element: <InspectionSpecification /> },
      // RBE-QUEUE-1: the review queue opens /qa-review-dashboard; the older Dashboards entry redirects there.
      { path: "/qa-review", element: <Navigate to="/qa-review-dashboard" replace /> },
      { path: "/shift-board", element: <ShiftBoard /> },
      { path: "/dashboard-thresholds", element: <DashboardThresholds /> },
      { path: "/equipment-rooms", element: <EquipmentRooms /> },
      { path: "/live-batches", element: <LiveBatchMonitor /> },
      { path: "/control-charts", element: <ControlCharts /> },
      { path: "/graph-studio", element: <ApqrGraphStudio /> },
      { path: "/data-tables", element: <ApqrDataTables /> },
      { path: "/cpv", element: <CpvHome /> },
      { path: "/cpv/risk-bands", element: <CpvRiskBands /> },
      { path: "/cpv/plans", element: <CpvPlans /> },
      { path: "/cpv/plans/:planId", element: <CpvPlanDetail /> },
      { path: "/cpv/plans/:id/charts", element: <CpvPlanCharts /> },
      { path: "/cpv/plans/:planId/charts/:itemId", element: <CpvParameterPage /> },
      { path: "/cpv/signals", element: <CpvSignals /> },
      { path: "/cpv/signals/:id", element: <CpvSignal /> },
      { path: "/numbering", element: <NumberingConfig /> },
      { path: "/sop-documents", element: <SopDocuments /> },
      { path: "/required-attachments", element: <RequiredAttachments /> },
      { path: "/audit-trail-review", element: <AuditTrailReview /> },
      { path: "/record-exports", element: <RecordExports /> },
      { path: "/report-designer", element: <ReportDesignerList /> },
      { path: "/report-designer/versions/:versionId", element: <ReportDesignerDesign /> },
      { path: "/report-designer/versions/:versionId/preview", element: <ReportDesignerPreview /> },
      { path: "/report-designer/parts/:partId", element: <ReportDesignerPart /> },
      { path: "/plant-hierarchy", element: <PlantHierarchyManager /> },
      { path: "/location-nodes", element: <LocationNodePage /> },
      { path: "/location-levels", element: <LocationLevelSetupPage /> },
      {
        path: "/userConfig",
        element: <UserConfig2 />,
      },
      {
        path: "/user-management",
        element: <UserManagementDashboard />,
      },
      {
        path: "/training-configuration",
        element: <TrainingConfiguration />,
      },
      {
        path: "/company-settings",
        element: <CompanySettings />,
      },
      {
        path: "/shift-management",
        element: <ShiftManagement />,
      },
      {
        path: "/formBuilder",
        element: <FormBuilder />,
      },
      {
        path: "/addRecipeForm",
        element: <AddRecipeForm />,
      },
      {
        path: "/accessControl",
        element: <AccessControl />,
      },
      {
        path: "/password-policy",
        element: <PasswordPolicy />,
      },
      {
        path: "/active-sessions",
        element: <ActiveSessions />,
      },
      {
        path: "/session-history",
        element: <SessionHistory />,
      },
      {
        path: "/user-view/:userId",
        element: <UserSessionsView />,
      },
      {
        path: "/directory-access",
        element: <DirectoryAccess />,
      },
      {
        path: "/plant-configuration",
        element: <PlantConfiguration />,
      },
      {
        path: "/hierarchy-definition",
        element: <HierarchyDefinition />,
      },
      {
        path: "/hierarchy-levels",
        element: <HierarchyLevelsDefinition />,
      },

      {
        path: "/batch",
        element: <Batch />,
      },
      {
        path: "/sample-testing",
        element: <SampleTesting />,
      },
      {
        path: "/batch-logbook",
        element: <BatchLogBook />,
      },
      {
        path: "/cleaning-logs",
        element: <CleaningLogs />,
      },
      {
        path: "/equipment-logs",
        element: <EquipmentLogs />,
      },
      {
        path: "/ingredients",
        element: <AddIngredient />,
      },
      {
        path: "/parameters",
        element: <AddData />,
      },
      {
        path: "/product",
        element: <Product />,
      },
      {
        path: "/sampling",
        element: <AddSample />,
      },
      {
        path: "/notifications",
        element: <NotificationPage />,
      },
      {
        path: "/calendar",
        element: <Calendar />,
      },
      {
        path: "/processesApproval",
        element: <AllProcessesByRecipeId />,
      },
      {
        path: "/recipeMetadata",
        element: <RecipeMetaData />,
      },
      {
        path: "/approveProcess",
        element: <ApproveProcess />,
      },
      {
        path: "/batch-approval",
        element: <Navigate to="/batch-workflow" replace />,
      },
      {
        path: "/audit-report",
        element: <Audit />,
      },
      {
        path: "/change-history-audit",
        element: <ChangeHistoryAudit />,
      },
      {
        path: "/check-cleaning",
        element: <CheckCleaning />,
      },

      {
        path: "/sensor",
        element: <Sensor />,
      },
      {
        path: "/sensor-monitoring",
        element: <SensorMonitoringDashboard />,
      },
      {
        path: "/bom",
        element: <BOM />,
      },
      {
        path: "/bal-material",
        // 2026-11-01 (owner) · Material Balance is a modal on the Batches list (Completed → Actions); the page is gone.
        element: <Navigate to="/batches" replace />,
      },
      {
        path: "/recipe-approval",
        element: <RecipeApproval />,
      },
      {
        path: "/approvals",
        element: <MasterApprovalsTabs />,
      },
      { path: "/equipment-and-rooms", element: <EquipmentAndRoomsHub /> },
      { path: "/review-release", element: <ReviewReleaseHub /> },
      { path: "/traceability", element: <TraceabilityHub /> },
      { path: "/quality-cpv", element: <CpvHub /> },
      { path: "/audit-trail", element: <AuditTrailHub /> },
      { path: "/reports", element: <ReportsHub /> },
      { path: "/trend-analysis", element: <TrendAnalysisHub /> },
      { path: "/master-records", element: <MasterRecordsHub /> },
      { path: "/products", element: <ProductsHub /> },
      { path: "/settings/plant-equipment", element: <PlantEquipmentSettings /> },
      { path: "/settings/quality-setup", element: <QualitySetupSettings /> },
      { path: "/settings/people-access", element: <PeopleAccessSettings /> },
      { path: "/settings/approval-workflows", element: <ApprovalWorkflowsSettings /> },
      { path: "/settings/system", element: <SystemSettings /> },
      { path: "/settings/integrations", element: <IntegrationsSettings /> },
      {
        path: "/generate-qr",
        element: <GenerateQRs />,
      },
      {
        path: "/shifts",
        element: <ShiftsWorkspace />,
      },
      {
        path: "/shift-details",
        element: <Shift />,
      },
      {
        path: "/employee-availability",
        element: <EmployeeAvailability />,
      },
      {
        path: "/employee-preferences",
        element: <EmployeePreferences />,
      },
      {
        path: "/messages",
        element: <ScheduleNotificationPage />,
      },
      {
        path: "/alert-notify",
        element: <AlertNotificationPage />,
      },
      {
        path: "/dag-flow",
        element: <DAGFlowCopy />,
      },
      {
        path: "/edit-batch",
        element: <EditBatch />,
      },

      {
        path: "/url-config",
        element: <UrlsConfig />,
      },
      {
        path: "/material-config",
        element: <Inventory />,
      },
      {
        path: "/prod-config",
        element: <ProductConfig />,
      },
      {
        path: "/mfr",
        element: <MasterFormulaRecords />,
      },
      {
        path: "/scaled-copy",
        element: <ScaledCopy />,
      },
      {
        path: "/approval-processes",
        element: <ApprovalProcesses />,
      },
      {
        path: "/report-builder",
        element: <ReportBuilder />,
      },
      {
        path: "/equipment-config",
        element: <Equipment />,
      },
      {
        path: "/sample-config",
        element: <SampleConfig />,
      },
      {
        path: "/testing-qc",
        element: <TestingQC />,
      },
      {
        path: "/machine-config",
        element: <Machine />,
      },
      {
        path: "/machine-type-config",
        element: <MachineTypeConfig />,
      },
      {
        path: "/dcs-config",
        element: <DcsConfig />,
      },
      {
        path: "/param-config",
        element: <Parameter />,
      },
      {
        path: "/transfer-path-config",
        element: <TransferPathConfig />,
      },
      {
        path: "/process-control-config",
        element: <ProcessControlConfig />,
      },
      {
        path: "/control-category-config",
        element: <ControlCategoryConfig />,
      },
      {
        path: "/process-control-parameter-config",
        element: <ProcessControlParameterConfig />,
      },
      {
        path: "/licence-config",
        element: <Licence />,
      },

      {
        path: "/edit-process",
        element: <ProcessEdit />,
      },
      {
        path: "/event-logs",
        element: <EventLogs />,
      },
      // 2026-09-25 · the old ten-panel browse surface. The designer is what authors work in
      // now and it has taken this path, so this route is retired rather than left as a second
      // door onto the same records. Kept commented rather than deleted: its dialogs (Copy,
      // New version) are still reached from the designer's card menu, which now opens them
      // through `/recipe-view-legacy` below.
      // {
      //   path: "/recipe-view",
      //   element: <RecipeView />,
      // },
      {
        // The old surface, still reachable for the two dialogs the designer hands off to.
        path: "/recipe-view-legacy",
        element: <RecipeView />,
      },
      {
        path: "/support-mbmr",
        element: <SupportMBMR support_mbmr={true} />,
      },
      {
        path: "/library",
        element: <Library />,
      },
      {
        path: "/process-library",
        element: <ProcessLibrary />,
      },
      {
        path: "/process-library-approvals",
        element: <ProcessLibraryApproval />,
      },
      {
        path: "/qa-review-dashboard",
        element: <ReviewByException />,
      },
      {
        path: "/pendingRecipe",
        element: <PendingRecipe />,
      },
      {
        path: "/processApproval",
        element: <ProcessApproval />,
      },
      {
        path: "/processApproval/:process_id",
        element: <ProcessApprovalDetail />,
      },
      {
        path: "/operation-approval",
        // W1-9: the queue's read and its act (supervisor_review_operation) are "BMR Details" on the server; who may
        // counter-sign is the server's signoff.countersign POLICY, applied to the rows it returns.
        element: (
          <ModuleGate modules={["My Sign-off Queue"]}>
            <OperationApproval />
          </ModuleGate>
        ),
      },
      {
        path: "/prod-recipe-approval",
        element: <ProdRecipeApproval />,
      },
      {
        path: "/archive-recipe",
        // 2026-10-29 (owner) · the archived records are listed on the Records page itself (same cards, read only,
        // with Restore): the old page is retired and its link lands there. ArchiveRecipe stays a PackagingConfig tab.
        element: <Navigate to="/recipe-view?archived=1" replace />,
      },
      {
        path: "/app-setting",
        element: <ApplicationSettings />,
      },

      {
        path: "/graph-new",
        element: <GraphNew />,
      },
      {
        path: "/graph",
        element: <Graph />,
      },
      {
        // EXR-W2 W2-4: the client-side "all conditions" check is retired; starting is gated server-side.
        path: "/line-clearence",
        element: <Navigate to="/batches" replace />,
      },
      {
        path: "/dynamic-flow",
        element: <DynamicFlow />,
      },
      {
        path: "/standard-block",
        element: <StandardBlockDetails />,
      },
      {
        path: "/pack-config",
        element: <PackagingConfig />,
      },
      {
        path: "/pack-mbpr",
        element: <ViewPackRecipe />,
      },
      {
        path: "/mbpr-log",
        element: <PackagingLogs />,
      },
      {
        path: "/dispatch-logs",
        element: <PackDispatchLogs />,
      },
      {
        path: "/process_manage",
        element: <ProcessManage />,
      },
      {
        path: "/continuous_prod",
        element: <ContineousProd />,
      },
      {
        path: "/standard-form",
        element: <StandardBlockForm />,
      },
      {
        path: "/signature-names",
        element: <SignatureNames />,
      },
      {
        path: "/standard-block/:blockId/design",
        element: <StandardBlockDesigner />,
      },
      {
        path: "/standard-block/:blockId/design/:process_id",
        element: <StandardBlockDesigner />,
      },
      {
        path: "/temporary-operation",
        element: <TemporaryOperationForm />,
      },
      {
        path: "/rules",
        element: <RulesConfig />,
      },
      {
        path: "/rules-form",
        element: <RulesForm />,
      },
      {
        path: "/rules/ingredients",
        element: <IngredientsRules />,
      },
      {
        path: "/rules/monitoring",
        element: <MonitoringRules />,
      },
      {
        path: "/rules/sampling",
        element: <SamplingRules />,
      },
      {
        path: "/rules/product",
        element: <ProductRules />,
      },
      {
        path: "/rules/action-list",
        element: <ActionListRules />,
      },
      {
        path: "/rules/checklist",
        element: <ChecklistRules />,
      },
      {
        path: "/rules/line-clearance",
        element: <LineClearanceRules />,
      },
      {
        path: "/rules/api-calculation",
        element: <APICalculationRules />,
      },
      {
        path: "/rules/machine-setup",
        element: <MachineSetupRules />,
      },
      {
        path: "/refilling-logs",
        element: <RefillingLogs />,
      },
      // CT-07: Plant Cockpit became the Control Tower's Overview; the old path (and bookmarks) land there.
      { path: "/mbmr-dash", element: <Navigate to="/control-tower/overview" replace /> },
      { path: "/control-tower", element: <ControlTower /> },
      { path: "/control-tower/:tab", element: <ControlTower /> },
      { path: "/mbmr-dash/legacy", element: <MBMRDashboard /> },
      // EXR-W2 W2-4 (owner Q1, DD-W2-13): the PharmaFlow wizard is retired — its act endpoints never
      // existed. A bookmarked path lands on the batch list.
      { path: "/WorkflowProgress", element: <Navigate to="/batches" replace /> },
      { path: "/areaSelection", element: <Navigate to="/batches" replace /> },
      { path: "/areaConfig", element: <AreaConfig /> },
      { path: "/processFlow", element: <Navigate to="/batches" replace /> },
      { path: "/initialProdFlow", element: <Navigate to="/batches" replace /> },
      { path: "/processOverview", element: <Navigate to="/batches" replace /> },
      { path: "/cleaningTypes", element: <CleaningTypes /> },
      { path: "/workFlowBuilder", element: <WorkflowBuilder /> },
      { path: "/roomConfig", element: <RoomConfig /> },
      { path: "/master-config", element: <MasterDataConfig /> },
      {
        // RV9 · the rebuilt designer. Lives beside /mbmr while the ten-panel path is still
        // the one authoring writes through; RV6b is folded into finishing this.
        // 2026-09-25 · the designer answers to /recipe-view now: it IS the way a record is
        // viewed and authored, and the name people already know should land on it. The old
        // /record-designer paths stay beside it so existing links, bookmarks and anything
        // that still navigates by that name keep working.
        path: "/recipe-view",
        element: <RecordDesignerPicker />,
      },
      {
        path: "/recipe-view/from-document",
        element: <MasterRecordFromDocument />,
      },
      {
        path: "/recipe-view/sop-change-impact",
        element: <SopChangeImpact />,
      },
      {
        path: "/recipe-view/:recipe_id",
        element: <RecordDesignerRoute />,
      },
      {
        path: "/recipe-view/:recipe_id/view",
        element: <RecordView />,
      },
      {
        path: "/record-designer",
        element: <RecordDesignerPicker />,
      },
      {
        path: "/record-designer/:recipe_id",
        element: <RecordDesignerRoute />,
      },
      {
        // A Process Library block in the designer. Beside the record routes because it is
        // the same screen on a different subject — the block is the procedure a record is
        // built from, and authoring the two in different tools is what let them drift.
        path: "/library-designer/:process_id",
        element: <LibraryProcessDesignerRoute />,
      },
      {
        // The same record, read end to end and changed nowhere. Reviewers are read-only.
        path: "/record-designer/:recipe_id/view",
        element: <RecordView />,
      },
      {
        path: "/mbmr",
        element: <MBMR />,
      },
      {
        // Library (recipe-less) process operation config. The library process id
        // lives in the URL so it survives refresh (location.state does not).
        path: "/process-config/library/:libId",
        element: <MBMRConfig />,
      },
      {
        path: "/process-config/:recipe_id",
        element: <MBMRConfig />,
      },
      {
        // Recipe Building Blocks on one page: four kinds as tabs, each master a sub-tab.
        // The masters keep their own routes below for bookmarks and deep links.
        path: "/recipe-blocks",
        element: <RecipeBuildingBlocks />,
      },
      {
        path: "/process-type",
        element: <ProcessTypeConfig />,
      },
      {
        path: "/output-form-type",
        element: <OutputFormTypeConfig />,
      },
      {
        path: "/variant-type",
        element: <VariantTypeConfig />,
      },
      {
        path: "/material-type",
        element: <MaterialTypeConfig />,
      },
      {
        path: "/material-state",
        element: <MaterialStateConfig />,
      },
      {
        path: "/units-config",
        element: <UnitsConfig />,
      },
      {
        path: "/operation-type",
        element: <OperationTypeLibrary />,
      },
      {
        // One type in the designer's shape — the same idiom as a record, because they are the
        // same thing at two levels.
        path: "/operation-type/:typeId",
        element: <OperationTypeView />,
      },
      {
        // The original screen. Kept reachable — it holds the add/edit forms.
        path: "/operation-type/config/edit",
        element: <OperationTypeConfig />,
      },
      {
        path: "/workflow-creation/",
        element: <Workflow />,
      },
      {
        path: "/workflow-creation/:id",
        element: <Workflow />,
      },
      {
        path: "/work-flow",
        element: <WorkflowsList />,
      },
      {
        path: "/bmr-workflows",
        element: <BMRWorkflowsPage />,
      },
      {
        path: "/bmr-workflow-builder/:id",
        element: <BMRWorkflowBuilderPage />,
      },
      {
        path: "/workflow-update/:workflow_id",
        element: <UpdateWorkflow />,
      },
      {
        path: "/form",
        element: <FormTemp />,
      },
      {
        path: "/form-details/:id",
        element: <CreateForm />,
      },
      {
        path: "/view-form/:id",
        element: <ViewForm />,
      },
      {
        path: "/ticket",
        element: <TicketList />,
      },
      {
        path: "/ticket-creation",
        element: <TicketCreation />,
      },
      {
        path: "/ticket/:id",
        element: <TicketMainpage />,
      },
      {
        path: "/ebmr-control-charts",
        element: <EBMRControlCharts />,
      },
      {
        path: "/AllDetails",
        element: <DetailsPage />,
      },
      {
        path: "/actionListFields",
        element: <ActionListField />,
      },
      {
        path: "/checklistFields",
        element: <ChecklistFields />,
      },
      {
        path: "/test-type",
        element: <TestTypeField />,
      },
      {
        path: "/apicalc-field",
        element: <ApiCalcField />,
      },
      {
        path: "/formula-library",
        element: <FormulaLibrary />,
      },
      {
        path: "/formula-usage-config",
        element: <FormulaLibrary />,
      },
      {
        path: "/formula-domain-mapping-config",
        element: <FormulaDomainMappingConfig />,
      },
      { path: "/clearance-profiles", element: <ClearanceProfiles /> },
      { path: "/batch-scoring", element: <BatchScoring /> },
      {
        path: "/clearance-type",
        element: <ClearanceType />,
      },
      {
        path: "/batch-number-format",
        element: <BatchNumberFormatConfig />,
      },
      {
        path: "/mfr-mbmr-lock-config",
        element: <MfrMbmrLockConfig />,
      },
      {
        path: "/system-integration",
        element: <IntegrationsHub />,
      },
      {
        path: "/system-integration/legacy",
        element: <SystemIntegration />,
      },
      {
        path: "/integrations/status",
        element: <IntegrationStatus />,
      },
      {
        path: "/integrations/connect",
        element: <ConnectStudio />,
      },
      {
        path: "/integrations/:system",
        element: <ConnectorFlows />,
      },
      {
        path: "/plant-select",
        element: <PlantSelection />,
      },
      {
        path: "/workflow-automation/create",
        element: <WorkflowAutomationBuilder />,
      },
      {
        path: "/workflow-automation/edit/:id",
        element: <WorkflowAutomationBuilder />,
      },
      {
        path: "/workflow-automation",
        element: <WorkflowAutomation />,
      },
      {
        path: "/batch-genealogy",
        element: <GenealogyTree />,
      },
      {
        path: "/batch-genealogy-full",
        element: <BatchGenealogyFull />,
      },
      {
        path: "/batch-workflow",
        element: <BatchWorkflowTimeline />,
      },
      {
        path: "/my-actions",
        element: <MyActions />,
      },
      {
        path: "/workflow-design",
        element: <TemplateManager />,
      },
      {
        path: "/recall-analysis",
        element: <RecallAnalysis />,
      },
    ].filter(Boolean),
  };
};

export default MainRoutes;
